import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, LayoutTemplate, Plus, Trash2 } from "lucide-react";
import * as React from "react";

import { ApiError } from "@/api/client";
import { homeSections } from "@/api/endpoints";
import type { HomeSection } from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  EmptyState,
  ErrorState,
  FormError,
  Skeleton,
  Spinner,
} from "@/components/ui/feedback";
import { Input, Label } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { formatDateTime, humanize } from "@/lib/utils";

const SECTION_TYPES = [
  { label: "Category grid", value: "CATEGORY_GRID" },
  { label: "Featured categories", value: "FEATURED_CATEGORIES" },
  { label: "Banner carousel", value: "BANNER_CAROUSEL" },
  { label: "Nearby providers", value: "NEARBY_PROVIDERS" },
  { label: "Top rated providers", value: "TOP_RATED_PROVIDERS" },
  { label: "Active tenders", value: "ACTIVE_TENDERS" },
  { label: "Quick actions", value: "QUICK_ACTIONS" },
];

const AUDIENCES = [
  { label: "Everyone", value: "ALL" },
  { label: "Farmers", value: "CONSUMER" },
  { label: "Providers", value: "PROVIDER" },
];

export default function HomeLayout() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [creating, setCreating] = React.useState(false);

  const query = useQuery({ queryKey: ["home-sections"], queryFn: homeSections.list });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ["home-sections"] });
  }

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      homeSections.update(id, body),
    onSuccess: invalidate,
    onError: (err) =>
      toast.error(
        "Could not update the section",
        err instanceof ApiError ? err.message : undefined,
      ),
  });

  const reorder = useMutation({
    mutationFn: (order: string[]) => homeSections.reorder(order),
    onSuccess: invalidate,
    onError: (err) =>
      toast.error(
        "Could not reorder",
        err instanceof ApiError ? err.message : undefined,
      ),
  });

  const remove = useMutation({
    mutationFn: (id: string) => homeSections.remove(id),
    onSuccess: () => {
      toast.success("Section removed");
      invalidate();
    },
    onError: (err) =>
      toast.error(
        "Could not remove the section",
        err instanceof ApiError ? err.message : undefined,
      ),
  });

  const sections = query.data ?? [];

  function move(index: number, direction: -1 | 1) {
    const next = [...sections];
    const target = index + direction;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    reorder.mutate(next.map((s) => s.id));
  }

  return (
    <>
      <PageHeader
        title="App home layout"
        description="Compose what farmers see when they open the app. Sections render top to bottom in this order."
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus />
            Add section
          </Button>
        }
      />

      {query.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-20" />
          <Skeleton className="h-20" />
        </div>
      ) : query.isError ? (
        <ErrorState error={query.error} onRetry={() => query.refetch()} />
      ) : sections.length > 0 ? (
        <ol className="space-y-2">
          {sections.map((s: HomeSection, i) => (
            <li key={s.id}>
              <Card>
                <CardContent className="flex flex-wrap items-center gap-3 p-4">
                  <div className="flex flex-col gap-0.5">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6"
                      disabled={i === 0 || reorder.isPending}
                      onClick={() => move(i, -1)}
                      aria-label={`Move ${s.title} up`}
                    >
                      <ArrowUp className="size-3.5" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="size-6"
                      disabled={i === sections.length - 1 || reorder.isPending}
                      onClick={() => move(i, 1)}
                      aria-label={`Move ${s.title} down`}
                    >
                      <ArrowDown className="size-3.5" />
                    </Button>
                  </div>

                  <span className="tabular w-6 text-center text-xs text-muted-foreground">
                    {i + 1}
                  </span>

                  <div className="min-w-[12rem] flex-1">
                    <p className="font-medium">{s.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {humanize(s.section_type)} · up to {s.max_items} items
                      {s.title_hi ? ` · ${s.title_hi}` : ""}
                    </p>
                    {(s.starts_at || s.ends_at) && (
                      <p className="mt-0.5 text-[11px] text-muted-foreground">
                        {s.starts_at ? `From ${formatDateTime(s.starts_at)}` : ""}
                        {s.ends_at ? ` until ${formatDateTime(s.ends_at)}` : ""}
                      </p>
                    )}
                  </div>

                  <Badge variant="outline">{humanize(s.audience)}</Badge>

                  {/* is_live folds in the scheduling window; is_active is just
                      the switch, so a scheduled-off section reads correctly. */}
                  <Badge variant={s.is_live ? "good" : "default"}>
                    {s.is_live ? "Live" : s.is_active ? "Scheduled" : "Hidden"}
                  </Badge>

                  <Button
                    variant="outline"
                    size="sm"
                    disabled={update.isPending}
                    onClick={() =>
                      update.mutate({ id: s.id, body: { is_active: !s.is_active } })
                    }
                  >
                    {s.is_active ? "Hide" : "Show"}
                  </Button>

                  <Button
                    variant="ghost"
                    size="icon"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Remove "${s.title}" from the home screen? This takes effect immediately.`,
                        )
                      ) {
                        remove.mutate(s.id);
                      }
                    }}
                    aria-label={`Remove ${s.title}`}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </CardContent>
              </Card>
            </li>
          ))}
        </ol>
      ) : (
        <Card>
          <EmptyState
            icon={LayoutTemplate}
            title="The home screen has no sections yet"
            description="Add a section to control what farmers see when they open the app."
            action={
              <Button onClick={() => setCreating(true)}>
                <Plus />
                Add the first section
              </Button>
            }
          />
        </Card>
      )}

      <Card className="mt-4">
        <CardHeader>
          <CardTitle>How this reaches the app</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-muted-foreground">
            The app reads <code className="tabular">GET /api/v1/home/layout</code> on
            launch and renders these sections in order. Nothing here is hardcoded in
            the app, so changes ship without an App Store or Play release.
          </p>
        </CardContent>
      </Card>

      {creating && <CreateSectionDialog onClose={() => setCreating(false)} />}
    </>
  );
}

function CreateSectionDialog({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [title, setTitle] = React.useState("");
  const [titleHi, setTitleHi] = React.useState("");
  const [type, setType] = React.useState("FEATURED_CATEGORIES");
  const [audience, setAudience] = React.useState("ALL");
  const [maxItems, setMaxItems] = React.useState(8);
  const [error, setError] = React.useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      homeSections.create({
        section_type: type,
        title: title.trim(),
        title_hi: titleHi.trim() || null,
        audience,
        max_items: maxItems,
      }),
    onSuccess: () => {
      toast.success("Section added", "It is live on the app home screen now.");
      void queryClient.invalidateQueries({ queryKey: ["home-sections"] });
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Could not add the section."),
  });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add a home section</DialogTitle>
          <DialogDescription>
            New sections go to the bottom; reorder them afterwards.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            create.mutate();
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor="type">Section type</Label>
            <Select
              value={type}
              onValueChange={setType}
              options={SECTION_TYPES}
              aria-label="Section type"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="title">Heading (English)</Label>
            <Input
              id="title"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Popular services near you"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="title-hi">Heading (Hindi)</Label>
            <Input
              id="title-hi"
              value={titleHi}
              onChange={(e) => setTitleHi(e.target.value)}
              placeholder="लोकप्रिय सेवाएं"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="audience">Audience</Label>
              <Select
                value={audience}
                onValueChange={setAudience}
                options={AUDIENCES}
                aria-label="Audience"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="max-items">Max items</Label>
              <Input
                id="max-items"
                type="number"
                min={1}
                max={50}
                value={maxItems}
                onChange={(e) => setMaxItems(Number(e.target.value))}
              />
            </div>
          </div>

          <FormError message={error} />

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !title.trim()}>
              {create.isPending && <Spinner />}
              Add section
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
